import { trackById } from 'src/app/shared/utils/track-by.util';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { SelectPanelWidthDirective } from "../../../../_helpers/select-panel-width.directive";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDialog } from "@angular/material/dialog";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { Subject, forkJoin, Observable, of } from "rxjs";
import { takeUntil, debounceTime, switchMap, map, tap, catchError } from "rxjs/operators";
import { Lookup } from "src/app/models/lookup.model";
import { AgentLookup } from "src/app/models/agentLookup.model";
import { AgentJurisdiction } from "src/app/models/agent-jurisdiction.model";
import { SampleTypeEnum } from "src/app/enums/sampleType.enum";
import { AgentService } from "src/app/services/agent.service";
import { AgentGroupService } from "src/app/services/agentGroup.service";
import { SampleSurveyTypeService } from "src/app/services/sample-survey-type.service";
import { OelTypeService } from "src/app/services/oel-type.service";
import { JurisdictionService } from "src/app/services/jurisdiction.service";
import { AgentJurisdictionService } from "src/app/services/agent-jurisdiction.service";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { ExceedanceTimePeriod } from "src/app/models/exceedance-compliance.model";
import {
    ExceedanceComplianceFilterValues,
    EXCEEDANCE_FILTER_DEFAULTS,
    TIME_PERIOD_OPTIONS,
    SAMPLE_CATEGORIES,
    SAMPLE_TYPES,
    resolveExceedanceWindow,
    toDisplayRange,
} from "./exceedance-compliance-widget.constants";
import { SearchableList } from "../_shared/searchable-list";
import { MultiSelectClearComponent } from "../_shared/multi-select-clear/multi-select-clear.component";
import { CustomDateRangeController, TimePeriodOptionWithRange } from "../_shared/custom-date-range-controller";
import nameOf from "src/app/shared/form/nameOf.helper";

interface TranslatableLookup {
    id: number;
    name: string;
}

interface AgentCascadeResult {
    jurisdictionIds: Set<string>;
    oelTypeIds: Set<string>;
}

export function computeEffectiveAgentIds(
    selectedAgentIds: string[],
    selectedAgentGroupIds: string[],
    groupAgentIds: ReadonlyMap<number | string, ReadonlySet<string>>,
): string[] {
    const ids = new Set<string>(selectedAgentIds);
    selectedAgentGroupIds.forEach(groupId => {
        groupAgentIds.get(groupId)?.forEach(id => ids.add(id));
    });
    return [...ids];
}

@Component({
    standalone: true,
    selector: "app-exceedance-compliance-filters",
    imports: [
    FormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    NgxMatSelectSearchModule,
    MultiSelectClearComponent,
    SelectPanelWidthDirective
],
    templateUrl: "./exceedance-compliance-filters.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./exceedance-compliance-filters.component.scss"]
})
export class ExceedanceComplianceFiltersComponent implements OnInit, OnChanges, OnDestroy {
    @Input() mode: "inline" | "config" = "inline";
    @Input() values: ExceedanceComplianceFilterValues = {
        ...EXCEEDANCE_FILTER_DEFAULTS,
    };
    @Input() effectiveOrgId: string | null = null;
    @Output() filterChange =
        new EventEmitter<ExceedanceComplianceFilterValues>();

    private destroy$ = new Subject<void>();
    private readonly agentCascade$ = new Subject<void>();
    private dateRangeCtrl: CustomDateRangeController;
    private loadedEffectiveOrgId: string | null = null;
    private agentDetailCache = new Map<string, AgentCascadeResult>();
    private groupAgentIds = new Map<number | string, Set<string>>();
    private groupMembershipsLoaded = false;
    private readonly agentIdsField = nameOf<ExceedanceComplianceFilterValues>(x => x.selectedAgentIds);
    private readonly agentGroupIdsField = nameOf<ExceedanceComplianceFilterValues>(x => x.selectedAgentGroupIds);
    private readonly surveyTypeIdsField = nameOf<ExceedanceComplianceFilterValues>(x => x.selectedSurveyTypeIds);

    allowedSampleTypes = new Set<SampleTypeEnum>();
    disabledSampleTypeIds = new Set<number>();

    agents = new SearchableList<AgentLookup>(
        (a, q) => !!a.name?.toLowerCase().includes(q),
        () => this.values.selectedAgentIds,
    );
    agentGroups = new SearchableList<Lookup>(
        (g, q) => !!g.name?.toLowerCase().includes(q),
        () => this.values.selectedAgentGroupIds,
    );
    surveyTypes = new SearchableList<Lookup>(
        (t, q) => !!t.name?.toLowerCase().includes(q),
        () => this.values.selectedSurveyTypeIds,
    );
    oelTypes = new SearchableList<Lookup>((o, q) => !!o.name?.toLowerCase().includes(q));
    jurisdictions = new SearchableList<Lookup>((j, q) => !!j.name?.toLowerCase().includes(q));
    sampleCategories = new SearchableList<TranslatableLookup>(
        (c, q) => this.translate.instant(c.name).toLowerCase().includes(q),
    );
    sampleTypes = new SearchableList<TranslatableLookup>(
        (s, q) => this.translate.instant(s.name).toLowerCase().includes(q),
    );

    readonly ExceedanceTimePeriod = ExceedanceTimePeriod;
    timePeriodOptionsWithRange: TimePeriodOptionWithRange[] = [];

    get customStartDateObj(): Date | undefined { return this.dateRangeCtrl.startDateObj; }
    get customEndDateObj(): Date | undefined { return this.dateRangeCtrl.endDateObj; }

    get customTimePeriodLabel(): string {
        return this.dateRangeCtrl.formatLabel(this.values.customStartDate, this.values.customEndDate);
    }

    constructor(
        private agentService: AgentService,
        private agentGroupService: AgentGroupService,
        private sampleSurveyTypeService: SampleSurveyTypeService,
        private oelTypeService: OelTypeService,
        private jurisdictionService: JurisdictionService,
        private agentJurisdictionService: AgentJurisdictionService,
        private dateFormatService: DateFormatService,
        private translate: TranslateService,
        dialog: MatDialog,
    ) {
        this.dateRangeCtrl = new CustomDateRangeController(dialog, dateFormatService, translate);
    }

    ngOnInit(): void {
        this.timePeriodOptionsWithRange = this.dateRangeCtrl.buildOptionsWithRanges(
            TIME_PERIOD_OPTIONS,
            ExceedanceTimePeriod.Custom,
            (period, now) => toDisplayRange(
                resolveExceedanceWindow(period, now),
                seconds => this.dateFormatService.convertTimestampToDate(seconds),
            ),
        );
        this.sampleCategories.setItems([...SAMPLE_CATEGORIES]);
        this.sampleTypes.setItems([...SAMPLE_TYPES]);
        this.dateRangeCtrl.syncFromStrings(this.values.customStartDate, this.values.customEndDate);
        this.subscribeAgentCascade();
        this.loadDropdowns();
        this.loadJurisdictions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.dateRangeCtrl.syncFromStrings(this.values.customStartDate, this.values.customEndDate);
        if (changes["effectiveOrgId"] && this.effectiveOrgId !== this.loadedEffectiveOrgId) {
            this.agentDetailCache.clear();
            this.groupAgentIds.clear();
            this.groupMembershipsLoaded = false;
            this.loadJurisdictions();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onFieldChange(
        key: keyof ExceedanceComplianceFilterValues,
        value: unknown,
    ): void {
        this.values = { ...this.values, [key]: value };
        if (key === this.agentIdsField || key === this.agentGroupIdsField) {
            this.recomputeAllowedSampleTypes();
            this.values = this.resetInvalidSampleType(this.values);
            if (key === this.agentIdsField) {
                this.agents.syncSelection();
            } else {
                this.agentGroups.syncSelection();
            }
            this.agentCascade$.next();
        }
        if (key === this.surveyTypeIdsField) {
            this.surveyTypes.syncSelection();
        }
        this.filterChange.emit(this.values);
    }

    clearAgents(): void {
        this.onFieldChange("selectedAgentIds", []);
    }

    clearAgentGroups(): void {
        this.onFieldChange("selectedAgentGroupIds", []);
    }

    clearSurveyTypes(): void {
        this.onFieldChange(this.surveyTypeIdsField, []);
    }

    private subscribeAgentCascade(): void {
        this.agentCascade$.pipe(
            debounceTime(200),
            switchMap(() => this.resolveAgentCascade()),
            takeUntil(this.destroy$),
        ).subscribe({ next: result => this.applyAgentCascade(result) });
    }

    private resolveAgentCascade(): Observable<AgentCascadeResult | null> {
        if (!this.values.selectedAgentIds.length && !this.values.selectedAgentGroupIds.length) {
            return of(null);
        }
        return this.ensureGroupMemberships().pipe(
            switchMap(() => {
                const effectiveAgentIds = this.getEffectiveAgentIds();
                return effectiveAgentIds.length ? this.resolveAgentDetails(effectiveAgentIds) : of(null);
            }),
        );
    }

    private getEffectiveAgentIds(): string[] {
        return computeEffectiveAgentIds(
            this.values.selectedAgentIds,
            this.values.selectedAgentGroupIds,
            this.groupAgentIds,
        );
    }

    private resetInvalidSampleType(
        values: ExceedanceComplianceFilterValues,
    ): ExceedanceComplianceFilterValues {
        if (
            values.selectedSampleTypeId != null &&
            this.allowedSampleTypes.size > 0 &&
            !this.allowedSampleTypes.has(values.selectedSampleTypeId)
        ) {
            return { ...values, selectedSampleTypeId: null };
        }
        return values;
    }

    private resolveAgentDetails(agentIds: string[]): Observable<AgentCascadeResult> {
        const cacheKey = [...agentIds].sort((a, b) => a.localeCompare(b)).join(',');
        const cached = this.agentDetailCache.get(cacheKey);
        if (cached) {
            return of(cached);
        }
        return forkJoin(agentIds.map(id => this.agentJurisdictionService.getAgentDetails(id))).pipe(
            map((responses: AgentJurisdiction[][]) => {
                const flat = responses.flat();
                const result: AgentCascadeResult = {
                    jurisdictionIds: new Set(flat.filter(r => r.jurisdictionId != null).map(r => r.jurisdictionId)),
                    oelTypeIds: new Set<string>(flat.map(r => r.oelTypeId).filter((id): id is string => id != null)),
                };
                if (flat.length) {
                    this.agentDetailCache.set(cacheKey, result);
                }
                return result;
            }),
        );
    }

    private applyAgentCascade(result: AgentCascadeResult | null): void {
        this.recomputeAllowedSampleTypes();
        if (result === null) {
            this.agentGroups.setBaseFilter(null);
            this.oelTypes.setBaseFilter(null);
            this.jurisdictions.setBaseFilter(null);
            return;
        }

        const selectedAgentIds = this.values.selectedAgentIds;
        const relevantGroupIds = new Set(
            this.agentGroups.items
                .filter(g => {
                    const members = this.groupAgentIds.get(g.id);
                    return !!members && selectedAgentIds.some(aid => members.has(aid));
                })
                .map(g => g.id)
        );
        this.agentGroups.setBaseFilter(relevantGroupIds.size > 0 ? relevantGroupIds : null);
        this.oelTypes.setBaseFilter(result.oelTypeIds.size > 0 ? result.oelTypeIds : null);
        this.jurisdictions.setBaseFilter(result.jurisdictionIds.size > 0 ? result.jurisdictionIds : null);

        let next = this.resetInvalidSampleType(this.values);
        if (
            next.selectedOelTypeId != null &&
            result.oelTypeIds.size > 0 &&
            !result.oelTypeIds.has(next.selectedOelTypeId)
        ) {
            next = { ...next, selectedOelTypeId: null };
        }
        if (
            next.selectedJurisdictionId != null &&
            result.jurisdictionIds.size > 0 &&
            !result.jurisdictionIds.has(next.selectedJurisdictionId)
        ) {
            next = { ...next, selectedJurisdictionId: null };
        }
        if (next.selectedAgentGroupIds.length > 0 && relevantGroupIds.size > 0) {
            const validGroupIds = next.selectedAgentGroupIds.filter(id => relevantGroupIds.has(id));
            if (validGroupIds.length !== next.selectedAgentGroupIds.length) {
                next = { ...next, selectedAgentGroupIds: validGroupIds };
            }
        }
        if (next !== this.values) {
            this.values = next;
            this.agentGroups.syncSelection();
            this.filterChange.emit(this.values);
        }
    }

    private recomputeAllowedSampleTypes(): void {
        const effectiveIds = new Set<number | string>(this.getEffectiveAgentIds());
        this.allowedSampleTypes = new Set(
            this.agents.items
                .filter((a) => effectiveIds.has(a.id))
                .map((a) => a.agentType),
        );
        this.disabledSampleTypeIds = new Set<number>(
            effectiveIds.size > 0
                ? this.sampleTypes.items
                      .filter((st) => !this.allowedSampleTypes.has(st.id))
                      .map((st) => st.id)
                : [],
        );
    }

    onTimePeriodChange(value: number): void {
        const prev = this.values.selectedTimePeriod;
        this.values = { ...this.values, selectedTimePeriod: value };

        if (value === ExceedanceTimePeriod.Custom) {
            this.openCustomDateDialog(prev);
            return;
        }

        this.values = { ...this.values, customStartDate: null, customEndDate: null };
        this.dateRangeCtrl.syncFromStrings(null, null);
        this.filterChange.emit(this.values);
    }

    openCustomDateDialog(previousTimePeriod: number): void {
        this.dateRangeCtrl.openDialog().subscribe(result => {
            if (!result) {
                this.values = { ...this.values, selectedTimePeriod: previousTimePeriod };
                this.filterChange.emit(this.values);
                return;
            }
            const { startIso, endIso } = this.dateRangeCtrl.applyResult(result);
            this.values = {
                ...this.values,
                selectedTimePeriod: ExceedanceTimePeriod.Custom,
                customStartDate: startIso,
                customEndDate: endIso,
            };
            this.filterChange.emit(this.values);
        });
    }

    public trackById = trackById;

    private loadDropdowns(): void {
        forkJoin({
            agents: this.agentService.getAll(""),
            agentGroups: this.agentGroupService.getAll(""),
            oelTypes: this.oelTypeService.getAll(""),
            surveyTypes: this.sampleSurveyTypeService.getAll("").pipe(catchError(() => of(null))),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ agents, agentGroups, oelTypes, surveyTypes }) => {
                this.agents.setItems(agents as AgentLookup[]);
                this.agentGroups.setItems(agentGroups);
                this.oelTypes.setItems(oelTypes);
                this.surveyTypes.setItems(surveyTypes ?? []);
                if (surveyTypes) {
                    this.pruneUnknownSurveyTypes();
                }
                this.recomputeAllowedSampleTypes();
                this.agentCascade$.next();
            });
    }

    private pruneUnknownSurveyTypes(): void {
        const valid = this.surveyTypes.prune(this.values.selectedSurveyTypeIds);
        if (valid) {
            this.values = { ...this.values, selectedSurveyTypeIds: valid };
            this.surveyTypes.syncSelection();
            this.filterChange.emit(this.values);
        }
    }

    private ensureGroupMemberships(): Observable<void> {
        if (this.groupMembershipsLoaded) {
            return of(undefined);
        }
        const groupIds = this.agentGroups.items.map(g => g.id);
        if (!groupIds.length) {
            this.groupMembershipsLoaded = true;
            return of(undefined);
        }
        return forkJoin(
            groupIds.map(id =>
                this.agentGroupService.getById(id).pipe(catchError(() => of(null))),
            ),
        ).pipe(
            tap(groups => {
                groups.forEach(group => {
                    if (group) {
                        this.groupAgentIds.set(group.id, new Set(group.agentIds ?? []));
                    }
                });
                this.groupMembershipsLoaded = true;
            }),
            map(() => undefined),
        );
    }

    private loadJurisdictions(): void {
        this.loadedEffectiveOrgId = this.effectiveOrgId;
        const request$ = this.effectiveOrgId != null
            ? this.jurisdictionService.getByOrgLevel(this.effectiveOrgId)
            : this.jurisdictionService.getAll("");
        request$
            .pipe(takeUntil(this.destroy$))
            .subscribe((jurisdictions) => {
                this.jurisdictions.setItems(jurisdictions);
                if (
                    this.values.selectedJurisdictionId != null &&
                    !jurisdictions.some((j) => j.id === this.values.selectedJurisdictionId)
                ) {
                    this.values = { ...this.values, selectedJurisdictionId: null };
                    this.filterChange.emit(this.values);
                }
            });
    }
}
